import React, { useEffect, useState } from 'react';

import './seat-request-panel.css';


// CC_REQUEST_A_SEAT (2026-06-08): the observer-side affordance for the
// PRIVATE-game recruitment flow. Renders ONLY when the viewer is a
// spectator of a PRIVATE game that has at least one recruitable AI seat
// (the public-game equivalent is the direct "Take this seat" tile on the
// games list — CC_MIDGAME_RECRUITMENT — not this in-game request). Tapping
// a recruitable seat sends RequestSeat; the panel then shows a pending
// state until the owner answers. A decline / void / typed-reject lands the
// polite notice and clears pending so the observer may request again.
//
// Data-source note: `recruitableSeats` is derived by GamePage from the
// observer's own board snapshot (gameplayer_set seats whose
// artificial_player is set); `isPrivate` from the game snapshot's
// `lobby_is_private` (live — the owner can flip the dial mid-game). The
// server re-validates every request, so this gate is UX only.
//
// COPY PLACEHOLDER — Michael owns every player-facing string here.
const REQUEST_HEADER = 'Want in?';
const REQUEST_BUTTON_LABEL = 'Request seat';
const PENDING_LABEL = 'Request sent — waiting for the host…';
const CANCEL_LABEL = 'Cancel request';
const DEFAULT_DECLINE_NOTICE = 'Your request was declined.';
const ALREADY_PENDING_CODE = 'already_pending';
// CC_REQUEST_A_SEAT_PROVE gap (c): the typed reject when the owner has
// turned seat requests off. COPY PLACEHOLDER — Michael owns the wording.
const REQUESTS_OFF_CODE = 'requests_off';
const REQUESTS_OFF_NOTICE = "The host isn't taking seat requests right now.";

export default function SeatRequestPanel({
    isSpectator, isPrivate, recruitableSeats, gameApi,
}) {
    // `pending` is true once a request is sent, until the owner answers
    // (or the observer cancels). `notice` carries a decline/reject string.
    const [pending, setPending] = useState(false);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        const onDeclined = (event) => {
            const detail = (event && event.detail) || {};
            setNotice(detail.message || DEFAULT_DECLINE_NOTICE);
            setPending(false);
        };
        const onRejected = (event) => {
            const detail = (event && event.detail) || {};
            // A server "already pending" duplicate-reject means our local
            // pending flag was lost (e.g. a reconnect dropped the UI state
            // while the connection stayed LOBBY_JOIN_REQUEST). Reflect
            // reality — flip pending back true so the Cancel affordance
            // returns — rather than showing a confusing error.
            if (detail.code === ALREADY_PENDING_CODE) {
                setPending(true);
                setNotice(null);
                return;
            }
            // The owner has requests turned off — surface the specific
            // notice rather than the generic decline (gap c).
            if (detail.code === REQUESTS_OFF_CODE) {
                setNotice(REQUESTS_OFF_NOTICE);
                setPending(false);
                return;
            }
            setNotice(DEFAULT_DECLINE_NOTICE);
            setPending(false);
        };
        const onApproved = () => {
            // We became a seated player; the panel is about to unmount as
            // isSpectator flips false. Clear local state defensively.
            setPending(false);
            setNotice(null);
        };
        window.addEventListener('seat-request-declined-event', onDeclined);
        window.addEventListener('seat-request-rejected-event', onRejected);
        window.addEventListener('seat-request-approved-event', onApproved);
        return () => {
            window.removeEventListener(
                'seat-request-declined-event', onDeclined);
            window.removeEventListener(
                'seat-request-rejected-event', onRejected);
            window.removeEventListener(
                'seat-request-approved-event', onApproved);
        };
    }, []);

    const seats = Array.isArray(recruitableSeats) ? recruitableSeats : [];

    // The gate: spectator + private + at least one recruitable seat.
    if (!isSpectator || !isPrivate || seats.length === 0) {
        return null;
    }

    const onRequest = (seat) => {
        if (gameApi && typeof gameApi.requestSeat === 'function') {
            gameApi.requestSeat(seat);
        }
        setNotice(null);
        setPending(true);
    };

    const onCancel = () => {
        if (gameApi && typeof gameApi.cancelSeatRequest === 'function') {
            gameApi.cancelSeatRequest();
        }
        setPending(false);
    };

    return (
        <div className="seat-request-panel" data-pending={pending ? 'true' : 'false'}>
            <div className="seat-request-panel-header">{REQUEST_HEADER}</div>
            {pending ? (
                <div className="seat-request-panel-pending">
                    <span>{PENDING_LABEL}</span>
                    <button
                        type="button"
                        className="seat-request-panel-cancel"
                        onClick={onCancel}
                    >
                        {CANCEL_LABEL}
                    </button>
                </div>
            ) : (
                <div className="seat-request-panel-seats">
                    {seats.map((seat) => (
                        <button
                            key={seat}
                            type="button"
                            className="seat-request-panel-request"
                            onClick={() => onRequest(seat)}
                        >
                            {`${REQUEST_BUTTON_LABEL} ${seat + 1}`}
                        </button>
                    ))}
                </div>
            )}
            {notice !== null && (
                <div className="seat-request-panel-notice">{notice}</div>
            )}
        </div>
    );
}
